/**
 * Billing API client.
 * Routes: /api/v1/tenants/{tenantId}/billing/...
 * Auth is handled by the central Axios interceptor in lib/api.ts — no duplication here.
 *
 * PCI SAQ-A: Payment method write routes (setup/setDefault/remove) have been
 * removed from the backend. Only the GET /payment-methods read endpoint remains.
 */
import api from '../lib/api';
import type {
  BillingOverviewResponse,
  SubscriptionDto,
  CreditBalanceDto,
  PaymentMethodDto,
  PackageDto,
  PaymentTransactionPagedResponse,
  TopUpCreditRequest,
  TopUpCreditResponse,
  CancelSubscriptionRequest,
  SubscriptionPurchaseRequest,
  SubscriptionPurchaseResponse,
  AdminTenantBillingResponse,
  GrantCreditRequest,
  GrantCreditResponse,
  BillingAccessSummary,
  UpdateBillingPhoneRequest,
} from '../types/billing';

function billingBase(tenantId: string): string {
  return `/api/v1/tenants/${tenantId}/billing`;
}

export const billingApi = {
  // ── Read ──────────────────────────────────────────────────────────────────

  getOverview(tenantId: string, signal?: AbortSignal): Promise<BillingOverviewResponse> {
    return api
      .get<BillingOverviewResponse>(`${billingBase(tenantId)}/overview`, { signal })
      .then((r) => r.data);
  },

  /**
   * Access-gate summary — server-authoritative Entitled/Blocked verdict.
   * Cached 30s server-side (docs/design/billing-access-gate.md); called on mount and on every
   * poll tick by `useBillingAccess` (`SubscriptionGuard` polls every 60s while a tenant is
   * Blocked, `BillingReturnPage` polls every 3s while waiting for a just-completed purchase to
   * land) and again right after a dormant-purchase success (`useRefreshBillingAccess`) to lift
   * the guard without waiting for the next login/refresh cycle.
   */
  getAccess(tenantId: string, signal?: AbortSignal): Promise<BillingAccessSummary> {
    return api
      .get<BillingAccessSummary>(`${billingBase(tenantId)}/access`, { signal })
      .then((r) => r.data);
  },

  getSubscriptions(tenantId: string, signal?: AbortSignal): Promise<SubscriptionDto[]> {
    return api
      .get<SubscriptionDto[]>(`${billingBase(tenantId)}/subscriptions`, { signal })
      .then((r) => r.data);
  },

  getCredits(tenantId: string, signal?: AbortSignal): Promise<CreditBalanceDto[]> {
    return api
      .get<CreditBalanceDto[]>(`${billingBase(tenantId)}/credits`, { signal })
      .then((r) => r.data);
  },

  getPackages(tenantId: string, signal?: AbortSignal): Promise<PackageDto[]> {
    return api
      .get<PackageDto[]>(`${billingBase(tenantId)}/packages`, { signal })
      .then((r) => r.data);
  },

  getPaymentMethods(tenantId: string, signal?: AbortSignal): Promise<PaymentMethodDto[]> {
    return api
      .get<PaymentMethodDto[]>(`${billingBase(tenantId)}/payment-methods`, { signal })
      .then((r) => r.data);
  },

  getTransactions(
    tenantId: string,
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<PaymentTransactionPagedResponse> {
    return api
      .get<PaymentTransactionPagedResponse>(`${billingBase(tenantId)}/transactions`, {
        params: { page, pageSize },
        signal,
      })
      .then((r) => r.data);
  },

  // ── Manage (TenantAdmin + Billing.Write) ──────────────────────────────────

  topUpCredit(
    tenantId: string,
    appCode: string,
    request: TopUpCreditRequest,
  ): Promise<TopUpCreditResponse> {
    return api
      .post<TopUpCreditResponse>(
        `${billingBase(tenantId)}/credits/${appCode}/top-up`,
        request,
      )
      .then((r) => r.data);
  },

  cancelSubscription(
    tenantId: string,
    subscriptionId: string,
    request: CancelSubscriptionRequest,
  ): Promise<void> {
    return api
      .post(`${billingBase(tenantId)}/subscriptions/${subscriptionId}/cancel`, request)
      .then(() => undefined);
  },

  /**
   * Sets the tenant's phone (AB#2193). Lives under the `billing` sub-router — TenantAdminOnly +
   * Billing.Write, callable by a Blocked tenant without tripping the 402 access gate — rather
   * than a general tenant-profile route. 422 `tenant.validation.phoneInvalid` when the value
   * does not normalize to a Turkish mobile number; 204 on success.
   */
  updateBillingPhone(tenantId: string, request: UpdateBillingPhoneRequest): Promise<void> {
    return api.put(`${billingBase(tenantId)}/phone`, request).then(() => undefined);
  },

  /**
   * Initiate a subscription purchase (AB#1237).
   * ENFORCED: returns chargeRequired=true + redirectUrl → caller must redirect browser.
   * DORMANT: returns chargeRequired=false + subscriptionId → subscription created directly.
   * RedirectUrl contains provider session tokens — NEVER log or render in DOM.
   */
  purchaseSubscription(
    tenantId: string,
    request: SubscriptionPurchaseRequest,
  ): Promise<SubscriptionPurchaseResponse> {
    return api
      .post<SubscriptionPurchaseResponse>(
        `${billingBase(tenantId)}/subscriptions/purchase`,
        request,
      )
      .then((r) => r.data);
  },

  // ── Admin (SuperAdmin only) ───────────────────────────────────────────────

  adminGetTenantBilling(tenantId: string, signal?: AbortSignal): Promise<AdminTenantBillingResponse> {
    return api
      .get<AdminTenantBillingResponse>(`/api/v1/admin/tenants/${tenantId}/billing`, { signal })
      .then((r) => r.data);
  },
  /** Grants credits to a tenant without a charge. 409 on an idempotency key reused with a different body. */
  adminGrantCredit(
    tenantId: string,
    appCode: string,
    request: GrantCreditRequest,
  ): Promise<GrantCreditResponse> {
    return api
      .post<GrantCreditResponse>(
        `/api/v1/admin/tenants/${tenantId}/billing/credits/${encodeURIComponent(appCode)}/grant`,
        request,
      )
      .then((r) => r.data);
  },
};
